// Utilitário único de moeda (at-30).
//
// Motivação: os parsers espalhados pelo app assumem string mascarada com 2 casas
// ("R$ 80.668,00" → /\D/g → /100). Valor sem máscara ("80668", vindo de
// Float.toString() do banco ou digitação livre) vira 1/100 do valor real nesses
// parsers. Todo caminho novo de moeda passa por aqui.

/** Limiar do aviso de divergência entre valor protegido e valor FIPE (±20%). */
export const LIMIAR_AVISO_VALOR_PROTEGIDO = 0.2;

/** Formata um número como "R$ 1.234,56" (sem depender de locale do ambiente). */
export const numeroParaMoeda = (valor: number | null | undefined): string => {
  if (valor === null || valor === undefined || isNaN(Number(valor))) return '';
  const [inteiro, decimais] = Number(valor).toFixed(2).split('.');
  const inteiroComPontos = inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `R$ ${inteiroComPontos},${decimais}`;
};

/**
 * Digitação livre: mantém dígitos, pontos de milhar e UMA vírgula com até 2 casas.
 *
 * Não reposiciona separadores enquanto se digita — é o que permite corrigir o meio
 * do valor. Máscara "em centavos" (dígitos empurrados da direita) reescreve o campo
 * a cada tecla, o cursor pula para o fim e o campo fica, na prática, ineditável.
 * Normalizar no blur com `normalizarMoeda`.
 */
export const digitarMoeda = (value: string): string => {
  const limpo = (value ?? '').replace(/R\$/g, '').replace(/[^\d.,]/g, '');
  const [inteiro, ...resto] = limpo.split(',');
  const inteiroLimitado = inteiro.slice(0, 15);
  if (resto.length === 0) return inteiroLimitado;
  return `${inteiroLimitado},${resto.join('').replace(/\./g, '').slice(0, 2)}`;
};

/** Normaliza o que foi digitado para a forma mascarada ("R$ 45.000,00"). */
export const normalizarMoeda = (value: string): string => {
  const numero = parseMoeda(value);
  return isNaN(numero) ? '' : numeroParaMoeda(numero);
};

/**
 * Parse por estrutura. Aceita os formatos que circulam no app:
 * "R$ 80.668,00" | "80668,00" (vírgula decimal) → 80668
 * "80668.00" (ponto decimal — formato do valorFipe da tela de cotação) → 80668
 * "80.668" (ponto de milhar) → 80668 · "80668" → 80668. NaN se vazio/inválido.
 */
export const parseMoeda = (value: string | number | null | undefined): number => {
  if (value === null || value === undefined || value === '') return NaN;
  if (typeof value === 'number') return value;
  const limpo = value.replace(/R\$/g, '').replace(/\s/g, '').trim();

  if (limpo.includes(',')) {
    // formato BR: ponto é milhar, vírgula é decimal
    return parseFloat(limpo.replace(/\./g, '').replace(',', '.'));
  }

  const pontos = limpo.match(/\./g)?.length ?? 0;
  if (pontos === 1 && /\.\d{1,2}$/.test(limpo)) {
    // um único ponto com 1-2 dígitos ao final: ponto decimal ("80668.00");
    // milhar sempre agrupa 3 dígitos ("80.668"), que cai no ramo abaixo
    return parseFloat(limpo);
  }

  // sem vírgula e sem ponto decimal: pontos (se houver) são milhar
  return parseFloat(limpo.replace(/\./g, ''));
};

/**
 * true quando |valor − referencia| / referencia excede o limiar.
 * Valores ausentes/inválidos nunca acusam divergência (aviso é opcional, não trava).
 */
export const divergeAcimaDoLimiar = (
  valor: string,
  referencia: string,
  limiar: number = LIMIAR_AVISO_VALOR_PROTEGIDO,
): boolean => {
  const a = parseMoeda(valor);
  const b = parseMoeda(referencia);
  if (isNaN(a) || isNaN(b) || b === 0) return false;
  return Math.abs(a - b) / b > limiar;
};
